import React, { useState, useEffect } from "react";

function DamageCalculator({ calculatorId = "default" }) {
  const [selectedDiscId, setSelectedDiscId] = useState("");
  const [selectedPA, setSelectedPA] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(30);
  const [selectedWeaponId, setSelectedWeaponId] = useState("");
  const [paList, setPaList] = useState([]);
  const [maxHP, setMaxHP] = useState(4655);
  const [charATP, setCharATP] = useState(2383);
  const [weaponAtpAtZero, setWeaponAtpAtZero] = useState(1085);
  const [wepVariance, setWepVariance] = useState(0);
  const [weaponATP, setWeaponATP] = useState(1300);
  const [selectedWeapon, setSelectedWeapon] = useState(null);
  const [weaponList, setWeaponList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [enemyDFP, setEnemyDFP] = useState(630);
  const [paModifier1, setPaModifier1] = useState(920);
  const [paModifier2, setPaModifier2] = useState(920);
  const [paModifier3, setPaModifier3] = useState(920);
  const [elementModifier, setElementModifier] = useState(50);
  const [atpBuffModifier, setAtpBuffModifier] = useState(1.0);
  const [zalureDebuff, setZalureDebufffModifier] = useState(1.0);
  const [paBoost, setPaBoostModifier] = useState(1.2);
  const [fullCustomPower, setFullCustomPowerModifier] = useState(0);
  const [weaponElementPlus, setWeaponElementPlus] = useState(0);
  const [rightHandPlus, setRightHandPlus] = useState(0);
  const [revengeBlaster, setRevengeBlaster] = useState(1.0);
  const [justAttackDamagePlus, setJustAttackDamagePlus] = useState(0);

  // Load PA data
  const loadPA = async (disc_id) => {
    if (!disc_id) return;

    const [paData, metadata] = await Promise.all([
      fetch(`https://psucta.com/api/pa-meta/pa/${disc_id}.json`).then((res) => {
        if (!res.ok) throw new Error(`PA file not found: ${disc_id}`);
        return res.json();
      }),
      fetch("https://psucta.com/api/pa-meta/pa_metadata.json").then((res) => res.json()),
    ]);

    const levelData = paData.data?.find((entry) => entry.level === selectedLevel - 1);
    const meta = metadata.find((entry) => entry.disc_id === disc_id);
    setSelectedPA({ ...paData, metadata: meta || {} });

    if (levelData?.combo_data && Array.isArray(levelData.combo_data)) {
      setPaModifier1(levelData.combo_data[0]?.atp_modifier ?? 0);
      setPaModifier2(levelData.combo_data[1]?.atp_modifier ?? 0);
      setPaModifier3(levelData.combo_data[2]?.atp_modifier ?? 0);
    } else {
      const mod = levelData?.atp_modifier ?? 0;
      setPaModifier1(mod);
      setPaModifier2(0);
      setPaModifier3(0);
    }
  };

  const handleWeaponChange = (id) => {
    setSelectedWeaponId(id);

    const weaponTypeCode = id.slice(2, 4); // extract the type from item ID
    const weaponTypeMap = {
      "01": "sword",
      "02": "knuckle",
      "03": "spear",
      "04": "double_saber",
      "05": "axe",
      "06": "twin_saber",
      "07": "twin_dagger",
      "08": "twin_claw",
      "09": "saber",
      "0A": "dagger",
      "0B": "claw",
      "0C": "whip",
      "0D": "slicer",
      "0E": "rifle",
      "0F": "shotgun",
      "10": "longbow",
      "11": "grenade_launcher",
      "12": "laser_cannon",
      "13": "twin_handgun",
      "14": "handgun",
      "15": "crossbow",
      "16": "card",
      "17": "machine_gun",
      "1B": "rcsm",
      "18": "rod",
      "19": "wand",
      "1A": "tcsm",
      // add more mappings as needed
    };
    const weaponType = weaponTypeMap[weaponTypeCode] || "unknown";

    fetch(`https://psucta.com/api/weapon/weapon/${weaponType}/${id}.json`)
      .then((res) => res.json())
      .then((weaponData) => {
        setSelectedWeapon({ ...weaponData, item_id: id });
        console.log("Weapon Data: " + id);
        const maxATP = weaponData.stat?.max_attack || [];
        const paBoost = weaponData.stat?.modifier?.pa_damage || [];
        const variance = weaponData.stat?.attack_variance || [];
        if (maxATP.length > 0) {
          setWeaponAtpAtZero(maxATP[0]);
          setWeaponATP(maxATP[maxATP.length - 1]);
          setPaBoostModifier(paBoost);
          setWepVariance(variance);
        }
      })
      .catch(console.error);
  };
  useEffect(() => {
    fetch("https://psucta.com/api/pa-meta/pa_metadata.json")
      .then((res) => res.json())
      .then(setPaList)
      .catch(console.error);

    fetch("https://psucta.com/api/weapon/weapon/index.json")
      .then((res) => res.json())
      .then((weaponArray) => {
        const filtered = weaponArray.map(w => ({ ...w, item_id: w.item_id }));
        setWeaponList(filtered);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedDiscId) {
      loadPA(selectedDiscId);
    }
  }, [selectedDiscId, selectedLevel]);

  const filteredWeapons = weaponList.filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Buff tables (same as before)
  const shiftaLevels = [
    { label: "None", value: 1.0 },
    { label: "Lv1", value: 1.11 },
    { label: "Lv2", value: 1.15 },
    { label: "Lv3", value: 1.19 },
    { label: "Lv4", value: 1.23 },
    { label: "Lv5", value: 1.27 },
  ];

  const zalureLevels = [
    { label: "None", value: 1.0 },
    { label: "Lv1", value: 1.11 },
    { label: "Lv2", value: 1.15 },
    { label: "Lv3", value: 1.19 },
    { label: "Lv4", value: 1.23 },
    { label: "Lv5", value: 1.27 },
  ];

  const fullCustomPowerLevels = [
    { label: "None", value: 0 },
    { label: "Lv1", value: 0.10 },
    { label: "Lv2", value: 0.20 },
  ];

  const justAttackDamageLevels = [
    { label: "None", value: 0.0 },
    { label: "Lv1", value: 0.045 },
    { label: "Lv2", value: 0.09 },
  ];

  const weaponElementPlusLevels = [
    { label: "None", value: 0 },
    { label: "Lv1", value: 3 },
  ];

  const rightHandPlusLevels = [
    { label: "None", value: 0 },
    { label: "Lv1", value: 0.02 },
    { label: "Lv2", value: 0.04 },
  ];

  const revengeBlasterLevels = [
    { label: "None", value: 1.0 },
    { label: "Lv1", value: 1.20 },
  ];

  const minRevengeBlaster = Math.min(...revengeBlasterLevels.map((b) => b.value));
  const maxRevengeBlaster = Math.max(...revengeBlasterLevels.map((b) => b.value));

  // Damage calculation
  let element = elementModifier !== 0
    ? 1 + ((5 + elementModifier + weaponElementPlus) / 151.5)
    : 1;

  const baseDamagePart1 = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier1 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamagePart1Min = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP-wepVariance + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier1 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamagePart2 = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier2 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamagePart2Min = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP-wepVariance + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier2 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamagePart3 = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier3 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamagePart3Min = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP-wepVariance + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier3 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );
  const baseDamage = Math.floor(
    ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier1 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
    + ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier2 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
    + ((charATP + Math.floor(weaponAtpAtZero * rightHandPlus) + (weaponATP + weaponAtpAtZero * fullCustomPower)) /
      3.7 *
      (paModifier3 / 100) *
      element *
      atpBuffModifier *
      (1000 / (1000 + enemyDFP)) *
      zalureDebuff) *
    revengeBlaster *
    paBoost
  );

  const netDamagePart1 = Math.ceil(baseDamagePart1, 0);
  const netDamagePart1Min = Math.ceil(baseDamagePart1Min, 0);
  const netDamagePart2 = Math.ceil(baseDamagePart2, 0);
  const netDamagePart2Min = Math.ceil(baseDamagePart2Min, 0);
  const netDamagePart3 = Math.ceil(baseDamagePart3, 0);
  const netDamagePart3Min = Math.ceil(baseDamagePart3Min, 0);
  const netDamage = Math.ceil(netDamagePart1 + netDamagePart2 + netDamagePart3, 0);
  const critDamagePart1 = Math.ceil(netDamagePart1 * 1.5);
  const critDamagePart1Min = Math.ceil(netDamagePart1Min * 1.5);
  const critDamagePart2 = Math.ceil(netDamagePart2 * 1.5);
  const critDamagePart2Min = Math.ceil(netDamagePart2Min * 1.5);
  const critDamagePart3 = Math.ceil(netDamagePart3 * 1.5);
  const critDamagePart3Min = Math.ceil(netDamagePart3Min * 1.5);
  const critDamage = Math.ceil(netDamage * 1.5);
  const justAttackDamage = Math.ceil(critDamage * (1 + justAttackDamagePlus));
  const justAttackDamagePart1 = Math.ceil(critDamagePart1 * (1 + justAttackDamagePlus));
  const justAttackDamagePart1Min = Math.ceil(critDamagePart1Min * (1 + justAttackDamagePlus));
  const justAttackDamagePart2 = Math.ceil(critDamagePart2 * (1 + justAttackDamagePlus));
  const justAttackDamagePart2Min = Math.ceil(critDamagePart2Min * (1 + justAttackDamagePlus));
  const justAttackDamagePart3 = Math.ceil(critDamagePart3 * (1 + justAttackDamagePlus));
  const justAttackDamagePart3Min = Math.ceil(critDamagePart3Min * (1 + justAttackDamagePlus));

  return (
    <div style={{ maxWidth: "500px", margin: "0 auto" }}>
      <h3>Photon Art</h3>
      <select onChange={(e) => setSelectedDiscId(e.target.value)} value={selectedDiscId}>
        <option value="">-- Select PA --</option>
        {paList.map((pa) => (
          <option key={pa.disc_id} value={pa.disc_id}>
            {pa.name || pa.disc_id}
          </option>
        ))}
      </select>

      <div>
        <label>Select PA Level:</label>
        <input
          type="number"
          min="1"
          max="50"
          step="1"
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(Number(e.target.value))}
        />
      </div>

      <div className="form-group">
        <label>Search Weapon:</label>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <ul>
            {filteredWeapons.map((w) => (
              <li
                key={w.item_id}
                onClick={() => handleWeaponChange(w.item_id)}
                style={{ cursor: "pointer" }}
              >
                {w.name}
              </li>
            ))}
          </ul>
        )}
        {selectedWeapon && (
          <div className="weapon-info">
            <p>
              <strong>{selectedWeapon.name}</strong>
            </p>
            {/* <p>{selectedWeapon.description}</p> */}
          </div>
        )}
      </div>
      {/* {selectedPA && (
        <>
          <p><strong>Frames:</strong> {selectedPA.metadata?.part_frames?.join(", ")}</p>
          <p><strong>Speed Mods:</strong> {selectedPA.metadata?.speed_modifiers?.join(", ")}</p>
          <p><strong>Earliest Transitions:</strong> {selectedPA.metadata?.pa_1_to_2_earliest_frame} → {selectedPA.metadata?.pa_2_to_3_earliest_frame}</p>
        </>
      )} */}


      {/* Existing input fields omitted here for brevity (you can reuse what you had) */}
      <label>Character HP:</label>
      <input
        type="number"
        value={maxHP}
        onChange={(e) => setMaxHP(Number(e.target.value))}
      />
      <div>
        <label>Character ATP:</label>
        <input
          type="number"
          value={charATP}
          onChange={(e) => setCharATP(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Weapon ATP (0/10):</label>
        <input
          type="number"
          value={weaponAtpAtZero}
          onChange={(e) => setWeaponAtpAtZero(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Weapon ATP (x/10):</label>
        <input
          type="number"
          value={weaponATP}
          onChange={(e) => setWeaponATP(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Weapon Percentage:</label>
        <input
          type="number"
          step="1"
          value={elementModifier}
          onChange={(e) => setElementModifier(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Weapon PA Boost:</label>
        <input
          type="number"
          value={paBoost}
          onChange={(e) => setPaBoostModifier(Number(e.target.value))}
        />
      </div>

      <div>
        <label>PA (Part 1) Modifier:</label>
        <input
          type="number"
          step="0.01"
          value={paModifier1}
          onChange={(e) => setPaModifier1(Number(e.target.value))}
        />
      </div>
      <div>
        <label>PA (Part 2) Modifier:</label>
        <input
          type="number"
          step="0.01"
          value={paModifier2}
          onChange={(e) => setPaModifier2(Number(e.target.value))}
        />
      </div>
      <div>
        <label>PA (Part 3) Modifier:</label>
        <input
          type="number"
          step="0.01"
          value={paModifier3}
          onChange={(e) => setPaModifier3(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Enemy DFP:</label>
        <input
          type="number"
          value={enemyDFP}
          onChange={(e) => setEnemyDFP(Number(e.target.value))}
        />
      </div>
      <div>
        <label>Shifta Level:</label>
        <br />
        {shiftaLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`shiftaBuff-${calculatorId}`}
              value={buff.value}
              checked={atpBuffModifier === buff.value}
              onChange={() => setAtpBuffModifier(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Zalure Level:</label>
        <br />
        {zalureLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`zalureDebuff-${calculatorId}`}
              value={buff.value}
              checked={zalureDebuff === buff.value}
              onChange={() => setZalureDebufffModifier(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Full Custom Power:</label>
        <br />
        {fullCustomPowerLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`fcp-${calculatorId}`}
              value={buff.value}
              checked={fullCustomPower === buff.value}
              onChange={() => setFullCustomPowerModifier(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Just Attack Damage+:</label>
        <br />
        {justAttackDamageLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`justAttackDamagePlus-${calculatorId}`}
              value={buff.value}
              checked={justAttackDamagePlus === buff.value}
              onChange={() => setJustAttackDamagePlus(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Weapon Element+:</label>
        <br />
        {weaponElementPlusLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`weaponElementPlus-${calculatorId}`}
              value={buff.value}
              checked={weaponElementPlus === buff.value}
              onChange={() => setWeaponElementPlus(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Right Hand+:</label>
        <br />
        {rightHandPlusLevels.map((buff) => (
          <label key={buff.value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name={`rightHandPlus-${calculatorId}`}
              value={buff.value}
              checked={rightHandPlus === buff.value}
              onChange={() => setRightHandPlus(buff.value)}
            />
            {buff.label}
          </label>
        ))}
      </div>
      <div>
        <label>Revenge Blaster:</label>
        <br />
        <input
          type="range"
          min={minRevengeBlaster}
          max={maxRevengeBlaster}
          step={0.01}
          value={revengeBlaster}
          onChange={(e) => setRevengeBlaster(parseFloat(e.target.value))}
          style={{ width: "300px" }}
        />
      </div>

      <hr />
      <h4>Part 1 Damage: {Math.round(netDamagePart1Min)} - {Math.round(netDamagePart1)}</h4>
      <h4>Part 2 Damage: {Math.round(netDamagePart2Min)} - {Math.round(netDamagePart2)}</h4>
      <h4>Part 3 Damage: {Math.round(netDamagePart3Min)} - {Math.round(netDamagePart3)}</h4>
      <h4>Part 1 Crit Damage: {Math.round(critDamagePart1Min)} - {Math.round(critDamagePart1)}</h4>
      <h4>Part 2 Crit Damage: {Math.round(critDamagePart2Min)} - {Math.round(critDamagePart2)}</h4>
      <h4>Part 3 Crit Damage: {Math.round(critDamagePart3Min)} - {Math.round(critDamagePart3)}</h4>
      <h4>Part 1 JA Damage: {Math.round(justAttackDamagePart1Min)} - {Math.round(justAttackDamagePart1)}</h4>
      <h4>Part 2 JA Damage: {Math.round(justAttackDamagePart2Min)} - {Math.round(justAttackDamagePart2)}</h4>
      <h4>Part 3 JA Damage: {Math.round(justAttackDamagePart3Min)} - {Math.round(justAttackDamagePart3)}</h4>
      {/* <h4>Total Damage: {Math.round(netDamage)}</h4>
      <h4>Crit Damage: {Math.round(critDamage)}</h4>
      <h4>JA Damage: {Math.round(justAttackDamage)}</h4> */}
    </div>
  );
}

export default DamageCalculator;
